import { FC, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { Button } from '@ipc/ipc-components';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import Table from '../../components/Table/Table';
import { useCounterpartEmails, CounterpartEmail } from '../../../hooks/useCounterpartEmails';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: {
    operatorByInboundPpo?: string | null;
    operatorByOutboundPpo?: string | null;
  } | null;
  moduleName: string;
}

/**
 * Generic modal component to display counterpart contact emails
 * @param props Component props
 * @returns ContactModal component
 */
const ContactModal: FC<ContactModalProps> = ({ isOpen, onClose, row, moduleName }) => {
  // Determine inbound and outbound operators from row data
  const inboundOperator = row?.operatorByInboundPpo || '';
  const outboundOperator = row?.operatorByOutboundPpo || '';

  // Fetch counterpart emails
  const {
    data: emailsData,
    isLoading,
    isError,
  } = useCounterpartEmails({
    module: moduleName,
    inboundOperator,
    outboundOperator,
    enabled: isOpen && !!row && !!inboundOperator && !!outboundOperator,
  });

  // Handler to open email client for individual email
  const handleEmailClick = (email: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click event
    window.location.href = `mailto:${email}`;
  };

  // Handler to open email client with all emails in CC
  const handleContactAllRecipients = () => {
    if (emailsData?.emails && emailsData.emails.length > 0) {
      const allEmails = emailsData.emails.map((email) => email.email).join(',');
      window.location.href = `mailto:?cc=${allEmails}`;
    }
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'Email',
        accessor: 'email',
        width: '50%',
        Cell: ({ value }: { value: any }) => (
          <Link
            href={`mailto:${value}`}
            onClick={(e) => handleEmailClick(value, e)}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            {value}
          </Link>
        ),
      },
      {
        Header: 'Designated Operator',
        accessor: 'designatedOperator',
        width: '50%',
      },
    ],
    []
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      aria-labelledby="contact-dialog-title"
    >
      {/* Header */}
      <DialogTitle
        id="contact-dialog-title"
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Contact Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Counterpart emails for this file
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">Failed to load contact information</Alert>
        ) : emailsData?.emails && emailsData.emails.length > 0 ? (
          <Table
            data={emailsData.emails}
            columns={columns}
            page={0}
            pageSize={emailsData.emails.length}
            totalCount={emailsData.emails.length}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            moduleName="Contact Emails"
            viewMode={true}
            hideDefaultActions={true}
            isRowClickable={false}
            showPagination={false}
          />
        ) : (
          <Alert severity="info">No contact information available</Alert>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ padding: 2, justifyContent: 'space-between' }}>
        <Box>
          {emailsData?.emails && emailsData.emails.length > 0 && (
            <Button
              onClick={handleContactAllRecipients}
              variant="primary"
              size="small"
              startIcon={<EmailIcon />}
            >
              Contact All Recipients
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="secondary" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactModal;

